import React, { useEffect, useRef } from "react";
import "./styles.scss";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getTransectionDetails } from "../../redux/slices/subscriptionPaymentSlice";
import { refreshUserProfile } from "../../redux/slices/authSlice";

import VerifiedIcon from "../../assets/images/verified.gif";
import { Spinner } from "react-bootstrap";
import { Search, Calculator, Bookmark, Clock, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

const SuccessPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // const successPayment = useSelector(
  //   (state) => state.subscriptionPayment
  // );

  const transaction = useSelector(
    (state) => state.subscriptionPayment?.transectionDetails,
  );

  const { subscriptionSuccessLoading, transectionDetailsLoading } = useSelector(
    (state) => state.subscriptionPayment,
  );

  const parseStripeResponse = (value) => {
    if (!value) return null;

    let parsed = value;

    try {
      if (typeof parsed === "string") {
        parsed = JSON.parse(parsed);
      }
      if (typeof parsed === "string") {
        parsed = JSON.parse(parsed);
      }
    } catch (e) {
      console.error("Stripe JSON parse failed", e);
      return null;
    }

    return parsed;
  };

  const stripeData = transaction?.stripe_response
    ? parseStripeResponse(transaction.stripe_response)
    : null;

  const user = useSelector((state) => state.auth?.user);

  const sessionId = new URLSearchParams(window.location.search).get(
    "session_id",
  );

  const hasTracked = useRef(false);

  useEffect(() => {
    if (hasTracked.current) return;
    if (!transaction?.amount || !transaction?.plan?.name) return;

    hasTracked.current = true;

    if (typeof window !== "undefined" && window.fbq) {
      const amount = Number(transaction.amount);

      // Calculate predicted LTV (12 months)
      const predictedLTV =
        transaction?.plan?.interval === "month" ? amount * 12 : amount; 

      window.fbq("track", "Subscribe", {
        value: amount,
        currency: "USD",
        predicted_ltv: predictedLTV,
        content_name: `${transaction.plan.name} ${transaction.plan.interval} Subscription`,
      });
    }
  }, [transaction]);
  // console.log(transaction, "transaction");
  // console.log(stripeData, "stripeData");
  // console.log(stripeData.invoice_pdf, "invoice_pdf");

  useEffect(() => {
    if (!sessionId) {
      navigate("/");
      return;
    }
    // dispatch(subscriptionSuccess({ session_id: sessionId }));
    dispatch(getTransectionDetails({ session_id: sessionId }));
    // Refresh redux user so the just-applied plan_id from the Stripe webhook
    // is reflected immediately. refreshUserProfile updates state.auth.user
    // (and localStorage), so all subscribed components re-render.
    dispatch(refreshUserProfile());
  }, [sessionId, dispatch, navigate]);

  if (subscriptionSuccessLoading || transectionDetailsLoading) {
    // Skeleton layout — mirrors the shape of the real plan-overview card below
    // so the user perceives the page as building, not flashing empty.
    const skeletonBar = (width) => (
      <span
        className="skeleton-bar"
        style={{
          display: "inline-block",
          width,
          height: 14,
          background: "linear-gradient(90deg, var(--mmp-hover-bg) 0%, var(--mmp-border) 50%, var(--mmp-hover-bg) 100%)",
          backgroundSize: "200% 100%",
          borderRadius: 4,
          animation: "skeleton-shimmer 1.2s ease-in-out infinite",
        }}
      />
    );

    return (
      <div className="payments-success-wrapper">
        <style>{`
          @keyframes skeleton-shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}</style>
        <div className="success-content">
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted">
              Finalizing your subscription details…
            </p>
          </div>

          <div className="plan-card" style={{ marginTop: 24 }}>
            <h3>Your Plan Overview</h3>

            <div className="item-row main-heading">
              <span>Item</span>
              <span>Details</span>
            </div>

            {[
              "Transaction ID",
              "Plan",
              "Amount",
              "Subscription Date",
              "Invoice",
            ].map((label) => (
              <div className="item-row" key={label}>
                <span>{label}</span>
                <span>{skeletonBar(140)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const formatInterval = (interval) => {
    if (interval === "month") return "Monthly";
    if (interval === "year") return "Yearly";
    return "--";
  };

  const trialEndsAt = transaction?.trial_ends_at || transaction?.trial_ends_at;
  const trialEndsFmt = trialEndsAt
    ? new Date(trialEndsAt).toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
      })
    : null;

  const nextSteps = [
    {
      to: "/search",
      icon: <Search size={20} />,
      title: "Run your first city comparison",
      subtitle: "Open Search",
    },
    {
      to: "/salaries-paychecks",
      icon: <Calculator size={20} />,
      title: "Try the Hospital Salary Calculator",
      subtitle: "Calculator · Hospital Salary tool",
    },
    {
      to: "/search",
      icon: <Bookmark size={20} />,
      title: "Save your dream cities",
      subtitle: "Add city flow",
    },
  ];

  return (
    <div className="payments-success-wrapper">
      <div className="success-content">
        <h2 className="title">Subscription Successfully Activated</h2>

          <p className="welcome">
            Welcome, <span className="name">{user?.full_name}</span>
          </p>

          <p className="sub-text">
            Your Map My Pay{" "}
            {/* <span className="name">{user?.plan?.name} Subscription </span> */}
            <span className="name">{transaction?.plan?.name} Subscription </span>
            is Now Active. <br />
            You're All Set To Explore Deeper Insights For Your Nursing Career.
          </p>

          <div className="success-icon">
            <span>
              <img src={VerifiedIcon} alt="Verified" />
            </span>
          </div>

          <div className="plan-card">
            <h3>Your Plan Overview</h3>

            <div className="item-row main-heading">
              <span>Item</span>
              <span>Details</span>
            </div>

            <div className="item-row">
              <span>Transaction ID</span>
              <span>#{transaction?.transaction_id}</span>
            </div>

            <div className="item-row">
              <span>Plan</span>
              <span>
                {transaction?.plan?.name}{" "}
                {formatInterval(transaction?.plan?.interval)} Subscription
              </span>
            </div>

            <div className="item-row">
              <span>Amount</span>
              <span>
                {transaction?.plan?.name === "Pro Trial"
                  ? "Free"
                  : `$${transaction?.amount}/${transaction?.plan?.interval}`}
              </span>
            </div>

            <div className="item-row">
              <span>Subscription Date</span>
              <span>{transaction?.subscription_date}</span>
            </div>

            <div className="item-row">
              <span>Invoice</span>
              <span>
                {stripeData?.invoice_pdf ? (
                  <a
                    href={stripeData.invoice_pdf}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Download PDF
                  </a>
                ) : (
                  "Not Available"
                )}
              </span>
            </div>
          </div>

          <div className="next-steps">
            {nextSteps.map((step) => (
              <Link to={step.to} key={step.title} className="next-step-card">
                <span className="step-icon">{step.icon}</span>
                <span className="step-text">
                  <span className="step-title">{step.title}</span>
                  <span className="step-subtitle">{step.subtitle}</span>
                </span>
                <ChevronRight size={18} className="step-chevron" />
              </Link>
            ))}

            {trialEndsFmt && (
              <div className="trial-notice">
                <Clock size={16} className="trial-notice-icon" />
                <span>
                  Trial ends <strong>{trialEndsFmt}</strong>. We'll remind you
                  24 hours before.
                </span>
              </div>
            )}
          </div>

        <a href="/dashboard" className="dashboard-link">
          Take me to the dashboard
        </a>
      </div>
    </div>
  );
};

export default SuccessPage;
